// YS Removals — centralised business configuration.
// Update this file to change contact details across the whole site.

export const BRAND = {
    name: "YS Removals",
    tagline: "London House & Office Removals",
    domain: "ysremovals.com",
    description:
        "Reliable house, flat and office removals across London. Careful handling, clear quotations, WhatsApp updates — YS Removals.",
    // Central logo — original file supplied by the business. Update this path to swap
    // the mark everywhere across the site (header, footer, admin, hero, favicon).
    logo: "/ys-logo.png",
    logoAlt: "YS Removals — Your Man With Van",
    // Official Google Business Profile URL. Reviews section links to this and can be
    // used to fetch reviews / rating when a Places API key is added later.
    googleBusinessUrl:
        "https://www.google.com/search?q=YS+Removals&stick=H4sIAAAAAAAA_-NgU1I1qEhMSzE0Nko2TDI1MDQzskyxMqhISTE0S0oyMzEwNE5MMTQwWsTKHRmsEJSam1-WmFMMAMcvrVk3AAAA&hl=en-GB",
};

// PLACEHOLDER contact info. Replace with real details when confirmed.
export const CONTACT = {
    phoneDisplay: "07883 508077",
    phoneHref: "tel:+447883508077",
    whatsappDisplay: "07883 508077",
    whatsappNumber: "447883508077", // international format, no + / spaces
    email: "ysremovals.uk@gmail.com",
    emailHref: "mailto:ysremovals.uk@gmail.com",
    addressLine1: "London",
    addressLine2: "United Kingdom",
    hours: "Mon–Sun, 8:00 – 20:00",
};

// Official WhatsApp brand green — used for all WhatsApp buttons/icons.
export const WHATSAPP_GREEN = "#25D366";
export const WHATSAPP_GREEN_DARK = "#128C7E";
export const WHATSAPP_DEFAULT_MESSAGE = "Hello YS Removals, I would like a quotation for my move.";

export const SOCIAL = {
    facebook: "https://www.facebook.com/profile.php?id=61578485711361",
    instagram: "https://www.instagram.com/ysremovals/",
};

// Legal / company details.
export const LEGAL = {
    companyDisplayName: "YS REMOVALS LTD",
    companyNumber: "17238727",
    jurisdiction: "England and Wales",
    registeredOffice: "5 Essex Close, Romford",
    vatNumber: "[VAT number placeholder]",
};

export const ANALYTICS = {
    ga4Id: "", // e.g. "G-XXXXXXXXXX"
    metaPixelId: "",
    googleAdsId: "",
    searchConsoleTag: "",
};

export function waLink(message) {
    const m = encodeURIComponent(message || WHATSAPP_DEFAULT_MESSAGE);
    return `https://wa.me/${CONTACT.whatsappNumber}?text=${m}`;
}

/**
 * Builds a clearly formatted WhatsApp enquiry message from a quote-form payload.
 * Used by the WhatsApp-first quote flow. Any missing field is skipped so the
 * message stays readable regardless of which form (hero / multi-step) was used.
 */
export function buildQuoteWhatsAppMessage(values = {}) {
    const line = (label, val) => {
        if (val === undefined || val === null) return null;
        const v = typeof val === "boolean" ? (val ? "Yes" : "No") : String(val).trim();
        if (!v) return null;
        return `• ${label}: ${v}`;
    };
    const rows = [
        line("Full name", values.full_name),
        line("Phone", values.phone),
        line("Email", values.email),
        line("Service", values.service),
        line("Moving date", values.moving_date),
        line("Flexible date", values.flexible_date),
        line("Collection address", values.collection_address),
        line("Collection postcode", values.collection_postcode),
        line("Collection property type", values.collection_property_type),
        line("Delivery address", values.delivery_address),
        line("Delivery postcode", values.delivery_postcode),
        line("Delivery property type", values.delivery_property_type),
        line("Bedrooms", values.bedrooms),
        line("Boxes (approx.)", values.boxes),
        line("Packing assistance", values.packing_assistance),
        line("Large / heavy items", values.large_items),
        line("Additional details", values.additional_details),
    ].filter(Boolean);
    return [
        "Hello YS Removals, I would like a quotation for my move.",
        "",
        ...rows,
        "",
        "(Sent via ysremovals.com quote form)",
    ].join("\n");
}

export const SERVICES = [
    {
        slug: "house-removals",
        title: "House Removals",
        short: "Full-service moves for houses of any size across London.",
        icon: "Home",
    },
    {
        slug: "flat-removals",
        title: "Flat Removals",
        short: "Considerate flat moves — stairs, lifts and narrow access covered.",
        icon: "Building2",
    },
    {
        slug: "office-removals",
        title: "Office Removals",
        short: "Planned office relocations with minimal downtime.",
        icon: "Briefcase",
    },
    {
        slug: "man-and-van",
        title: "Man and Van",
        short: "Flexible support for smaller jobs, deliveries and short-notice moves.",
        icon: "Truck",
    },
    {
        slug: "furniture-delivery",
        title: "Furniture Delivery",
        short: "Careful pickup and delivery of new or used furniture.",
        icon: "Sofa",
    },
    {
        slug: "packing-assistance",
        title: "Packing Assistance",
        short: "Materials and hands-on help to pack your home properly.",
        icon: "Package",
    },
    {
        slug: "single-item-delivery",
        title: "Single Item Delivery",
        short: "One item? No problem — fast, careful single-item moves.",
        icon: "Boxes",
    },
    {
        slug: "student-moves",
        title: "Student Moves",
        short: "Affordable student moves between halls, flats and storage.",
        icon: "GraduationCap",
    },
];

export const EXTRA_SERVICES = [
    {
        slug: "same-day-removals",
        title: "Same-Day Removals",
        short: "Short-notice moves subject to availability.",
        icon: "Zap",
    },
];

export const AREAS = [
    "Central London", "North London", "South London", "East London", "West London",
    "Croydon", "Bromley", "Greenwich", "Lewisham", "Lambeth",
    "Southwark", "Wandsworth", "Hammersmith and Fulham", "Ealing", "Brent",
    "Barnet", "Enfield", "Haringey", "Hackney", "Newham",
    "Tower Hamlets", "Redbridge", "Barking and Dagenham", "Bexley", "Sutton",
    "Merton", "Kingston", "Richmond", "Hounslow", "Harrow", "Hillingdon",
];

export const TRUST_POINTS = [
    "London-wide service",
    "Flexible booking",
    "Careful handling",
    "Clear quotations",
    "Friendly communication",
];

export const IMAGES = {
    heroPrimary:
        "https://images.pexels.com/photos/7464684/pexels-photo-7464684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroLogo: "/ys-logo.png",
    services:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80",
    blog1:
        "https://images.unsplash.com/photo-1633505899118-4ca6bd143043?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtb3ZpbmclMjBib3hlcyUyMGxpdmluZyUyMHJvb20lMjBtaW5pbWFsJTIwYnJpZ2h0fGVufDB8fHx3aGl0ZXwxNzg0NjAyMTM3fDA&ixlib=rb-4.1.0&q=85",
    blog2:
        "https://images.unsplash.com/photo-1708704974484-3ca56db9384f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb3ZpbmclMjBib3hlcyUyMGxpdmluZyUyMHJvb20lMjBtaW5pbWFsJTIwYnJpZ2h0fGVufDB8fHx3aGl0ZXwxNzg0NjAyMTM3fDA&ixlib=rb-4.1.0&q=85",
};
